/**
 * MarkdownCodeEditor — Markdown-Quellmodus für statische Beiträge (#2242).
 *
 * Statische Beiträge halten ihren Inhalt als Markdown-String. Dafür ist ein
 * echter Texteditor richtig: jede Zeile, jeder Umbruch und jedes Einfügen
 * bleibt zeichengenau. CodeMirror 6 liefert das; die Formatierung kommt aus
 * demselben Scanner und denselben CSS-Klassen wie im Collab-Editor
 * (MarkdownSource.ts), damit Markdown im Portal überall gleich aussieht.
 *
 * Die Zeichen bleiben sichtbar, werden aber bereits formatiert dargestellt.
 * Der Text selbst wird nie umgeschrieben.
 */

import { useEffect, useRef } from 'react'
import { Annotation, EditorSelection, EditorState, Prec, type Extension } from '@codemirror/state'
import {
  Decoration,
  EditorView,
  ViewPlugin,
  keymap,
  placeholder as cmPlaceholder,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import {
  MD_TABLE_SKELETON,
  RE_BLOCK_PREFIX,
  advanceFence,
  initialScanState,
  nextListMarker,
  scanMarkdownLine,
  type MarkdownToolbarApi,
} from '../utils/markdownScan'

interface Props {
  value: string
  onChange: (next: string) => void
  /** Markierter Text und Zeile des Auswahlbeginns; leer, wenn nichts markiert ist. */
  onSelection?: (text: string, line: number | null) => void
  /** Dateien wurden in den Editor gezogen. Liefert die Markdown-Schnipsel
   *  (ein Eintrag pro Datei), die an der Stelle eingefügt werden. */
  onDropFiles?: (files: File[]) => Promise<string[]>
  onDragState?: (over: boolean) => void
  /** Übergibt die Werkzeugleisten-Schnittstelle; null beim Abbau. */
  onReady?: (api: MarkdownToolbarApi | null) => void
  placeholder?: string
}

/** Kennzeichnet Transaktionen, die den Wert von außen übernehmen. */
const External = Annotation.define<boolean>()

/** Zeilen ober- und unterhalb des Ausschnitts, die mitformatiert werden. */
const VIEWPORT_MARGIN = 2000

function buildDecorations(view: EditorView): DecorationSet {
  const doc = view.state.doc
  const from = Math.max(0, view.viewport.from - VIEWPORT_MARGIN)
  const to = Math.min(doc.length, view.viewport.to + VIEWPORT_MARGIN)
  const state = initialScanState()
  const ranges = []
  for (let n = 1; n <= doc.lines; n++) {
    const line = doc.line(n)
    if (line.from > to) break
    if (line.to < from) {
      // Außerhalb des Ausschnitts zählt nur, ob ein Fence offen ist.
      advanceFence(line.text, state)
      continue
    }
    const res = scanMarkdownLine(line.text, state)
    if (res.block) ranges.push(Decoration.line({ class: res.block }).range(line.from))
    for (const s of res.spans) {
      if (s.to > s.from) ranges.push(Decoration.mark({ class: s.cls }).range(line.from + s.from, line.from + s.to))
    }
  }
  return Decoration.set(ranges, true)
}

const markdownDecorations = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view)
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged) this.decorations = buildDecorations(u.view)
    }
  },
  { decorations: (v) => v.decorations },
)

// --- Befehle: Markdown-Zeichen einfügen -----------------------------------

function wrapSelection(view: EditorView, marker: string): boolean {
  const tr = view.state.changeByRange((range) => {
    if (range.empty) {
      return {
        changes: { from: range.from, insert: marker + marker },
        range: EditorSelection.cursor(range.from + marker.length),
      }
    }
    const text = view.state.sliceDoc(range.from, range.to)
    return {
      changes: { from: range.from, to: range.to, insert: marker + text + marker },
      range: EditorSelection.range(range.from + marker.length, range.to + marker.length),
    }
  })
  view.dispatch(view.state.update(tr, { scrollIntoView: true, userEvent: 'input' }))
  return true
}

function setBlockPrefix(view: EditorView, prefix: string): boolean {
  const line = view.state.doc.lineAt(view.state.selection.main.head)
  const existing = RE_BLOCK_PREFIX.exec(line.text)?.[0] ?? ''
  const next = existing === prefix ? '' : prefix
  view.dispatch({
    changes: { from: line.from, to: line.from + existing.length, insert: next },
    userEvent: 'input',
  })
  return true
}

function insertLink(view: EditorView): boolean {
  const { from, to, empty } = view.state.selection.main
  const text = empty ? 'Text' : view.state.sliceDoc(from, to)
  const urlFrom = from + text.length + 3
  view.dispatch({
    changes: { from, to, insert: `[${text}](url)` },
    selection: EditorSelection.range(urlFrom, urlFrom + 3),
    userEvent: 'input',
  })
  return true
}

/** Fügt einen Block auf eigenen Zeilen ein, mit Leerzeile davor und danach. */
function insertBlock(view: EditorView, block: string, selectInner?: [number, number]): boolean {
  const pos = view.state.selection.main.head
  const line = view.state.doc.lineAt(pos)
  const lead = line.text.trim() === '' ? '' : '\n\n'
  const at = line.text.trim() === '' ? line.from : line.to
  const insert = `${lead}${block}\n`
  const start = at + lead.length
  view.dispatch({
    changes: { from: at, to: at, insert },
    selection: selectInner
      ? EditorSelection.range(start + selectInner[0], start + selectInner[1])
      : EditorSelection.cursor(start + block.length),
    scrollIntoView: true,
    userEvent: 'input',
  })
  return true
}

/** Enter in einer Liste: Marker fortführen, leeren Punkt beenden. */
function continueList(view: EditorView): boolean {
  const sel = view.state.selection.main
  if (!sel.empty) return false
  const line = view.state.doc.lineAt(sel.head)
  const next = nextListMarker(line.text)
  if (!next) return false
  if (next.emptyItem) {
    view.dispatch({ changes: { from: line.from, to: line.to, insert: '' }, userEvent: 'delete' })
    return true
  }
  if (sel.head !== line.to) return false
  view.dispatch({
    changes: { from: sel.head, insert: `\n${next.prefix}` },
    selection: EditorSelection.cursor(sel.head + 1 + next.prefix.length),
    scrollIntoView: true,
    userEvent: 'input',
  })
  return true
}

const markdownKeymap = Prec.high(
  keymap.of([
    { key: 'Mod-b', run: (v) => wrapSelection(v, '**') },
    { key: 'Mod-i', run: (v) => wrapSelection(v, '*') },
    { key: 'Mod-e', run: (v) => wrapSelection(v, '`') },
    { key: 'Mod-Shift-s', run: (v) => wrapSelection(v, '~~') },
    { key: 'Mod-k', run: insertLink },
    { key: 'Mod-Alt-0', run: (v) => setBlockPrefix(v, '') },
    { key: 'Mod-Alt-1', run: (v) => setBlockPrefix(v, '# ') },
    { key: 'Mod-Alt-2', run: (v) => setBlockPrefix(v, '## ') },
    { key: 'Mod-Alt-3', run: (v) => setBlockPrefix(v, '### ') },
    { key: 'Mod-Shift-8', run: (v) => setBlockPrefix(v, '- ') },
    { key: 'Mod-Shift-7', run: (v) => setBlockPrefix(v, '1. ') },
    { key: 'Mod-Shift-9', run: (v) => setBlockPrefix(v, '- [ ] ') },
    { key: 'Mod-Shift-b', run: (v) => setBlockPrefix(v, '> ') },
    { key: 'Enter', run: continueList },
  ]),
)

const baseTheme = EditorView.theme({
  '&': { fontSize: '15px', color: '#444', backgroundColor: 'transparent' },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': { fontFamily: 'inherit', lineHeight: '1.6', overflow: 'visible' },
  '.cm-content': { padding: '0', caretColor: '#111', letterSpacing: '0.3px' },
  '.cm-line': { padding: '0' },
  '.cm-placeholder': { color: '#b0b0b0' },
})

export function MarkdownCodeEditor({ value, onChange, onSelection, onDropFiles, onDragState, onReady, placeholder }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const viewRef = useRef<EditorView | null>(null)
  // Die Rückrufe wechseln bei jedem Render; der Editor wird nur einmal gebaut.
  const cb = useRef({ onChange, onSelection, onDropFiles, onDragState })
  useEffect(() => {
    cb.current = { onChange, onSelection, onDropFiles, onDragState }
  })

  useEffect(() => {
    if (!hostRef.current) return
    const extensions: Extension[] = [
      history(),
      markdownKeymap,
      keymap.of([...defaultKeymap, ...historyKeymap]),
      EditorView.lineWrapping,
      baseTheme,
      markdownDecorations,
      EditorView.updateListener.of((u) => {
        if (u.docChanged && !u.transactions.some((t) => t.annotation(External))) {
          cb.current.onChange(u.state.doc.toString())
        }
        if (u.selectionSet || u.docChanged) {
          const sel = u.state.selection.main
          const text = sel.empty ? '' : u.state.sliceDoc(sel.from, sel.to).trim()
          cb.current.onSelection?.(text, text ? u.state.doc.lineAt(sel.from).number : null)
        }
      }),
      EditorView.domEventHandlers({
        dragenter(event) {
          if (event.dataTransfer?.types?.includes('Files')) {
            event.preventDefault()
            cb.current.onDragState?.(true)
          }
        },
        dragover(event) {
          if (event.dataTransfer?.types?.includes('Files')) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
          }
        },
        dragleave(event, view) {
          const rel = event.relatedTarget as Node | null
          if (!rel || !view.dom.contains(rel)) cb.current.onDragState?.(false)
        },
        drop(event, view) {
          const files = Array.from(event.dataTransfer?.files || [])
          if (files.length === 0 || !cb.current.onDropFiles) return false
          event.preventDefault()
          cb.current.onDragState?.(false)
          const pos = view.posAtCoords({ x: event.clientX, y: event.clientY }) ?? view.state.selection.main.head
          void cb.current.onDropFiles(files).then((inserts) => {
            const v = viewRef.current
            if (!v || inserts.length === 0) return
            const at = Math.min(pos, v.state.doc.length)
            const before = at > 0 ? v.state.sliceDoc(at - 1, at) : '\n'
            const insertion = (before !== '\n' ? '\n\n' : '') + inserts.join('\n\n') + '\n'
            v.dispatch({
              changes: { from: at, insert: insertion },
              selection: EditorSelection.cursor(at + insertion.length),
              scrollIntoView: true,
              userEvent: 'input',
            })
            v.focus()
          })
          return true
        },
      }),
    ]
    if (placeholder) extensions.push(cmPlaceholder(placeholder))

    const view = new EditorView({
      parent: hostRef.current,
      state: EditorState.create({ doc: value, extensions }),
    })
    viewRef.current = view
    onReady?.({
      wrap: (marker) => { view.focus(); wrapSelection(view, marker) },
      prefix: (prefix) => { view.focus(); setBlockPrefix(view, prefix) },
      link: () => { view.focus(); insertLink(view) },
      fence: () => { view.focus(); insertBlock(view, '```\ncode\n```', [4, 8]) },
      table: () => { view.focus(); insertBlock(view, MD_TABLE_SKELETON) },
    })
    return () => {
      onReady?.(null)
      view.destroy()
      viewRef.current = null
    }
    // Der Editor wird genau einmal gebaut; spätere Werte kommen über den
    // Abgleich unten, Rückrufe über cb.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Wert von außen (Laden, Konflikt, Speichern durch andere) übernehmen.
  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const current = view.state.doc.toString()
    if (current === value) return
    const head = Math.min(view.state.selection.main.head, value.length)
    view.dispatch({
      changes: { from: 0, to: current.length, insert: value },
      selection: EditorSelection.cursor(head),
      annotations: External.of(true),
    })
  }, [value])

  return <div ref={hostRef} className="md-code-editor md-source" />
}
